import { useId } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NewInput } from '../../PIM/common/NewInput';
import { NewTextArea } from '../../PIM/common/NewTextArea';
import type { TextOptionsProps } from './text-options.types';

/**
 * Options tab of the Textbox and Comment setup cards (Figma): a Character Limit box and the
 * placeholder the field shows while empty.
 * @param characterLimit - Current limit, or null for no limit.
 * @param placeholder - Current placeholder text.
 * @param placeholderLabel - Label above the placeholder input.
 * @param multiline - Whether the placeholder input is a text area (Comment).
 * @param disabled - Whether the inputs are locked.
 * @param onChange - Called with the changed option.
 * @returns The options block.
 */
function TextOptions({ characterLimit, placeholder, placeholderLabel, multiline = false, disabled = false, onChange }: TextOptionsProps) {
  const { t } = useTranslation('custom-module-system-canvas');
  const limitId = useId();
  const placeholderId = useId();

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '');
    onChange({ characterLimit: digits ? Number(digits) : null });
  };

  return (
    <div className="cm-text-options">
      <div className="cm-text-options__limit">
        <NewInput
          id={limitId}
          type="text"
          inputMode="numeric"
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIMIT')}
          value={characterLimit ?? ''}
          onChange={handleLimitChange}
          disabled={disabled}
        />
        <label className="cm-text-options__hint" htmlFor={limitId}>
          {t('CUSTOM_MODULE.SYSTEM_CANVAS.CHARACTER_LIMIT')}
        </label>
      </div>
      <div className="cm-field-setup__field">
        <label className="cm-field-setup__field-label" htmlFor={placeholderId}>
          {placeholderLabel}
        </label>
        {multiline ? (
          <NewTextArea
            id={placeholderId}
            rows={PLACEHOLDER_ROWS}
            resize="vertical"
            placeholder={placeholderLabel}
            value={placeholder}
            onChange={(event) => onChange({ placeholder: event.target.value })}
            disabled={disabled}
          />
        ) : (
          <NewInput
            id={placeholderId}
            type="text"
            placeholder={placeholderLabel}
            value={placeholder}
            onChange={(event) => onChange({ placeholder: event.target.value })}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}

export default TextOptions;

const PLACEHOLDER_ROWS = 8;
